import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { BackofficeSessionService } from './backoffice-session.service';
import {
  BackofficeTournamentResponse,
  BackofficeTournamentsResponse,
  BackofficeTournamentUpsertPayload
} from './backoffice-tournaments-api.types';
import { BracketReadResponse } from './tournaments-api.types';

@Injectable({ providedIn: 'root' })
export class BackofficeTournamentsApiService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(BackofficeSessionService);
  private readonly apiBaseUrl = environment.apiBaseUrl;

  list(): Observable<BackofficeTournamentsResponse> {
    return this.http.get<BackofficeTournamentsResponse>(`${this.apiBaseUrl}/api/backoffice/tournaments`, {
      headers: this.authHeaders()
    });
  }

  createTournament(payload: BackofficeTournamentUpsertPayload): Observable<BackofficeTournamentResponse> {
    return this.http.post<BackofficeTournamentResponse>(
      `${this.apiBaseUrl}/api/backoffice/tournaments`,
      { tournament: payload },
      { headers: this.authHeaders() }
    );
  }

  updateTournament(tournamentId: number, payload: BackofficeTournamentUpsertPayload): Observable<BackofficeTournamentResponse> {
    return this.http.patch<BackofficeTournamentResponse>(
      `${this.apiBaseUrl}/api/backoffice/tournaments/${tournamentId}`,
      { tournament: payload },
      { headers: this.authHeaders() }
    );
  }

  publishTournament(tournamentId: number): Observable<BackofficeTournamentResponse> {
    return this.http.post<BackofficeTournamentResponse>(
      `${this.apiBaseUrl}/api/backoffice/tournaments/${tournamentId}/publish`,
      {},
      { headers: this.authHeaders() }
    );
  }

  archiveTournament(tournamentId: number): Observable<BackofficeTournamentResponse> {
    return this.http.post<BackofficeTournamentResponse>(
      `${this.apiBaseUrl}/api/backoffice/tournaments/${tournamentId}/archive`,
      {},
      { headers: this.authHeaders() }
    );
  }

  unarchiveTournament(tournamentId: number): Observable<BackofficeTournamentResponse> {
    return this.http.post<BackofficeTournamentResponse>(
      `${this.apiBaseUrl}/api/backoffice/tournaments/${tournamentId}/unarchive`,
      {},
      { headers: this.authHeaders() }
    );
  }

  finishTournament(tournamentId: number): Observable<BackofficeTournamentResponse> {
    return this.http.post<BackofficeTournamentResponse>(
      `${this.apiBaseUrl}/api/backoffice/tournaments/${tournamentId}/finish`,
      {},
      { headers: this.authHeaders() }
    );
  }

  reopenTournament(tournamentId: number): Observable<BackofficeTournamentResponse> {
    return this.http.post<BackofficeTournamentResponse>(
      `${this.apiBaseUrl}/api/backoffice/tournaments/${tournamentId}/reopen`,
      {},
      { headers: this.authHeaders() }
    );
  }

  getBracket(tournamentId: number): Observable<BracketReadResponse> {
    return this.http.get<BracketReadResponse>(
      `${this.apiBaseUrl}/api/backoffice/tournaments/${tournamentId}/bracket`,
      { headers: this.authHeaders() }
    );
  }

  generateBracket(tournamentId: number, bracketType: string, drawMode: 'random' | 'seeded' | 'manual' | 'wheel'): Observable<BracketReadResponse> {
    return this.http.post<BracketReadResponse>(
      `${this.apiBaseUrl}/api/backoffice/tournaments/${tournamentId}/bracket/generate`,
      { bracket_type: bracketType, draw_mode: drawMode },
      { headers: this.authHeaders() }
    );
  }

  publishBracket(tournamentId: number): Observable<BracketReadResponse> {
    return this.http.post<BracketReadResponse>(
      `${this.apiBaseUrl}/api/backoffice/tournaments/${tournamentId}/bracket/publish`,
      {},
      { headers: this.authHeaders() }
    );
  }

  private authHeaders(): HttpHeaders {
    const token = this.session.token();
    return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
  }
}
